// Create a new file: src/sections/@dashboard/settings/ApiKeyManager.js

import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Stack,
  Button,
  Divider,
  TextField,
  Typography,
  CardHeader,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../../components/iconify';
import axios from '../../../utils/Axios';

export default function ApiKeyManager() {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [apiKeyData, setApiKeyData] = useState({
    hasApiKey: false,
    apiKeyCreatedAt: null,
  });
  const [newApiKey, setNewApiKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchApiKeyStatus();
  }, []);

  const fetchApiKeyStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/user/api-key');
      setApiKeyData(response.data);
    } catch (err) {
      setError(err?.messages?.[0] || err?.message || 'Failed to fetch API key status');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateApiKey = async () => {
    setGenerating(true);
    try {
      const response = await axios.post('/user/api-key');
      setNewApiKey(response.data.apiKey);
      setApiKeyData({
        hasApiKey: true,
        apiKeyCreatedAt: response.data.apiKeyCreatedAt,
      });
      setOpenDialog(true);
    } catch (err) {
      setError(err?.messages?.[0] || err?.message || 'Failed to generate API key');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevokeApiKey = async () => {
    setRevoking(true);
    try {
      await axios.delete('/user/api-key');
      setApiKeyData({
        hasApiKey: false,
        apiKeyCreatedAt: null,
      });
      setNewApiKey(null);
    } catch (err) {
      setError(err?.messages?.[0] || err?.message || 'Failed to revoke API key');
    } finally {
      setRevoking(false);
    }
  };

  const handleCopyApiKey = async () => {
    try {
      await navigator.clipboard.writeText(newApiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <Card>
      <CardHeader title="Generate your API key" />
      <CardContent>
        <Stack spacing={3}>
          <Typography variant="body1">
            Use this API key to connect FlowBookings with Zapier and other tools.
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {apiKeyData.hasApiKey ? (
            <>
              <Alert severity="info">
                You have an active API key generated on{' '}
                {new Date(apiKeyData.apiKeyCreatedAt).toLocaleString()}
              </Alert>
              <Typography variant="body2" color="text.secondary">
                You'll need this key when connecting FlowBookings to Zapier in Step 5.
              </Typography>
              <LoadingButton
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:trash-2-outline" />}
                loading={revoking}
                onClick={handleRevokeApiKey}
              >
                Revoke API Key
              </LoadingButton>
            </>
          ) : (
            <LoadingButton
              variant="contained"
              startIcon={<Iconify icon="eva:key-outline" />}
              loading={generating}
              onClick={handleGenerateApiKey}
            >
              Generate API Key
            </LoadingButton>
          )}
          {/* <Divider />

          <Typography variant="h6">How to use your API key</Typography>
          <Typography variant="body2">
            1. Copy your API key after generating it (you'll only see it once).
            <br />
            2. In Zapier, create a new connection and use this API key in the "API Key" field.
            <br />
            3. The API key allows access to your FlowBookings data through our API endpoints.
          </Typography> */}
        </Stack>
      </CardContent>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Your New API Key</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This is your new API key. Please copy it now - you won't be able to see it again!
          </DialogContentText>
          <TextField
            fullWidth
            value={newApiKey || ''}
            margin="dense"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopyApiKey} edge="end">
                    <Iconify icon="eva:copy-outline" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {copied && (
            <Alert severity="success" sx={{ mt: 2 }}>
              API key copied to clipboard!
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}